import TimeLayer from "./TimeLayer";

import moment from "moment-timezone";
import NysmTimeSeries2 from "../Dataset/NysmTimeSeries2";

import { multiPolygon, polygonize, bboxPolygon, difference } from '@turf/turf';
import { scaleLinear, contours as d3contours } from 'd3';
import GeoJSONFormat from "ol/format/GeoJSON";
import { getVectorContext } from 'ol/render.js';
import { transformExtent } from "ol/proj";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";


// The API returns a grid that extrapolates outside of NY State.
// This code creates a clipping layer so that contours can be clipped to NY State.
const latlonProjection = "EPSG:4326";
const clipCache = {};
const clippingFeature = async (mapProjection) => {
  if (clipCache[mapProjection]) {
    return clipCache[mapProjection];
  }
  const res = await fetch("/map-resources/nystate.geojson");
  const state_geojson = await res.json();
  
  // File comes in as LineString geometries.
  // It needs to be a single multipolygon for the clipping operation.
  const state_polygons = polygonize(state_geojson);
  const single_polygon = multiPolygon(state_polygons.features.map(f => f.geometry.coordinates));
  const GeoJSON_reader = new GeoJSONFormat({dataProjection: latlonProjection, featureProjection: mapProjection});
  const clipFeature = GeoJSON_reader.readFeature(single_polygon);
  clipCache[mapProjection] = clipFeature;
  return clipCache[mapProjection];
};



const gridDataToContours = (data, variable, {
  mapProjection,
  thresholds
}) => {
  const width = data.dims.lon;
  const height = data.dims.lat;
  const contours = d3contours()
    .size([width, height])
    .thresholds(thresholds);
  const sourceValues = data.data_vars[variable].data[0];
  const grid = new Array(width * height);
  let getVal = (i, j) => sourceValues[j][i]; // lon then lat
  if (data.data_vars[variable].dims[1] == 'lon') {
    getVal = (i, j) => sourceValues[i][j]; // lat then lon
  }
  for (let i = 0; i < width; i++) {
    for (let j = 0; j < height; j++) {
      const val = getVal(i, j);
      grid[i + j * width] = val;
    }
  }
  const polygons = contours(grid);

  // Scale polygons to lat/lon
  const minLat = data.coords.lat.data[0];
  const maxLat = data.coords.lat.data[height-1];
  const minLon = data.coords.lon.data[0];
  const maxLon = data.coords.lon.data[width-1];

  const lonScale = scaleLinear([0, width], [minLon, maxLon]);
  const latScale = scaleLinear([0, height], [minLat, maxLat]);
  const scaledPolygons = polygons.map(poly => {
    return {
      ...poly,
      coordinates: poly.coordinates.map(component => {
        return component.map(componentLine => {
          return componentLine.map(point => {
            return [lonScale(point[0]), latScale(point[1])];
          });
        });
      })
    };
  });


  const collection = {
    type: 'FeatureCollection',
    features: scaledPolygons.map(geometry => {
      return {
        type: "Feature",
        geometry: geometry,
        properties: {
          threshold: geometry.value
        }
      };
    })
  };

  const GeoJSON_reader = new GeoJSONFormat({dataProjection: latlonProjection, featureProjection: mapProjection});
  const contours_ol = GeoJSON_reader.readFeatures(collection);
  const extent = transformExtent([minLon, minLat, maxLon, maxLat], latlonProjection, mapProjection);
  
  return { contours: contours_ol, extent };
};


class NysmFreezingLineLayer extends TimeLayer {
  constructor(opt_options) {
    const options = opt_options || {};
    const baseOptions = Object.assign({}, options);
    delete baseOptions.mapProjection;
    super(baseOptions);

    this._dataset = "nysm";
    this._varid = "tair";
    this._mapProjection = options.mapProjection;
    this._datasetProvider = new NysmTimeSeries2(this._dataset);
    super.setStartTimeAllowableDelta(this._datasetProvider.realtime_lag);

    this._loaded_times = new Set();
    this._rtTimeManager = null;

    this.load = this.load.bind(this);
    this._realtime_data_received = this._realtime_data_received.bind(this);

    this._subscribedToTracking = false;
  }

  load(timeManager) {
    const now = moment.utc();
    let endTime = timeManager.end_date;
    const startTimeDelta = timeManager.start_date
      .clone()
      .subtract(this._datasetProvider.realtime_lag);
    // detect realtime request
    if (
      timeManager.isTracking ||
      now.clone().subtract(this._datasetProvider.realtime_lag) < endTime
    ) {
      endTime = null;
    }
    // if realtime request and we are tracking realtime, see if we can
    // skip this one
    if (
      endTime === null &&
      this._subscribedToTracking &&
      (timeManager.start_date.isSame(timeManager.end_date) ||
        [...this._loaded_times].sort()[0] <=
          timeManager.start_date
            .clone()
            .subtract(this._datasetProvider.data_interval)
            .valueOf())
    ) {
      return;
    }
    this._datasetProvider
      .get_data_in_time_range(this._varid, startTimeDelta, endTime)
      .then((result) => {
        const new_times = [];
        result.coords.time.data.forEach((time, i) => {
          const momentTime = moment.tz(time, "UTC");
          new_times.push(momentTime);
        });
        this._layer_times_received(new_times, timeManager);
      });
  }

  _layer_times_received(new_times, timeManager) {
    const startTime = timeManager.start_date;
    const endTime = timeManager.end_date;
    const startTimeDelta = timeManager.start_date
      .clone()
      .subtract(this._datasetProvider.realtime_lag);
    // Filter to include only the range we care about.
    let addedOneEarly = false;
    let times = [];
    try {
      times = new_times.reverse().filter((t) => {
        if (t.isSameOrAfter(startTime) && t.isSameOrBefore(endTime)) {
          if (t.isSame(startTime)) {
            // Don't need to add one early, flag it as done
            addedOneEarly = true;
          }
          return true;
        }
        if (
          t.isBefore(startTime) &&
          t.isSameOrAfter(startTimeDelta) &&
          !addedOneEarly
        ) {
          addedOneEarly = true;
          return true;
        }
        return false;
      });
    } catch (err) {
      console.error(err);
      times = [];
    }
    // Filter times down to those that don't already have layers (or have layers pending), then
    // create layers for any new times.
    const times_loaded = this.getLayers()
      .getArray()
      .map((l) => l.get("time").valueOf());
    times
      .filter((t) => times_loaded.indexOf(t.valueOf()) === -1)
      .forEach((t) => {
        this._loaded_times.add(t.valueOf());
        this._draw_layer(t).then(new_layer => {
          if (new_layer) {
            this.getLayers().push(new_layer);
          } else {
            console.warn("No layer generated");
          }
        });
      });
  }

  track_realtime(isTracking, timeManager) {
    if (!isTracking && this._subscribedToTracking) {
      this._datasetProvider.remove_realtime_tracker(
        this._realtime_data_received
      );
      this._rtTimeManager = null;
    } else if (isTracking && !this._subscribedToTracking) {
      this._rtTimeManager = timeManager;
      this._datasetProvider.add_realtime_tracker(this._realtime_data_received);
    }
    this._subscribedToTracking = isTracking;
  }

  _realtime_data_received(newTime, result) {
    this._layer_times_received([newTime], this._rtTimeManager);
  }

  async _draw_layer(time) {
    const clipFeature = await clippingFeature(this._mapProjection);
    const data = await this._datasetProvider.get_griddata_at_time("tair", time);
    NysmTimeSeries2.adjust_response(data, "first");
    const { contours, extent } = gridDataToContours(data, "tair", {
      mapProjection: this._mapProjection,
      thresholds: [0]
    });
    const contour = contours[0];

    // A "contour" is a polygon around the regions that are >= the threshold.
    // I want to shade the areas that are < the threshold.
    // Create a new polygon around the full extent of the 0C threshold and subtract
    // the 0C contour from it.
    const extentPolygon = bboxPolygon(extent);
    const aboveFreezingPolygon = {
      type: contour.getGeometry().getType(),
      coordinates: contour.getGeometry().getCoordinates()
    };
    let belowFreezingPolygon = null;
    if (aboveFreezingPolygon.coordinates.length === 0) {
      belowFreezingPolygon = extentPolygon;
    }
    else {
      belowFreezingPolygon = difference(extentPolygon, aboveFreezingPolygon);
    }
    belowFreezingPolygon = new GeoJSONFormat().readFeature(belowFreezingPolygon);
    const date_str = time === null ? "latest" : time.format("YYYY-MM-DDTHH:mm:ss.SSS");
    const contourLayerSource = new VectorSource({
      features: [belowFreezingPolygon],
    });
    const contourLayer = new VectorLayer({
      time: time,
      className: `contour-${date_str}`,
      opacity: 1,
      source: contourLayerSource,
      style: (feature, resolution) => {
        const color = "#0000ff";
        const fillColor = "#0000ff33";
        return [
          new Style({
            stroke: new Stroke({
              color: color,
              width: 4
            }),
            fill: new Fill({
              color: fillColor,
            })
          }),
          // new Style({
          //   text: new Text({
          //     text: "32",
          //     font: '12pt sans-serif',
          //     fill: new Fill({ color: "#0000ff"}),
          //     stroke: new Stroke({ color: "#ffffff", width: 4 }),
          //     placement: "line",
          //     overflow: true,
          //     repeat: 100,
          //     maxAngle: 360
          //   })
          // })
        ];
      }
    });

    const clip = true;
    if (clip) {
      // A fill has to be defined, but it's not actually rendered this way.
      const clipStyle = new Style({ fill: new Fill({ color: 'black' })});

      contourLayer.on('postrender', function (e) {
        const vectorContext = getVectorContext(e);
        const oldOperation = e.context.globalCompositeOperation;
        e.context.globalCompositeOperation = 'destination-in';
        vectorContext.drawFeature(clipFeature, clipStyle); // Anything outside of the multipolygon will be changed to transparent.
        e.context.globalCompositeOperation = oldOperation;
      });
    }
    return contourLayer;
  }
}

export default NysmFreezingLineLayer;
